import type { NodeOutput, PlanNode, PlanNodeLifecycleState, PlanState } from '@aline/common';
import {
  flattenPlanNodes,
  PLAN_NODE_LIFECYCLE_STATUSES,
  PLAN_STATE_STATUSES,
  resolvePriorNodeIds,
} from '@aline/common';

export type NodeStatus = PlanNodeLifecycleState['status'] | 'queued';

/**
 * Flattens the chat-harness assistant turn into a single markdown string
 * suitable for the V1-style ChatPanelMarkdown pipeline. Composition:
 *
 *   1. Planner-authored markdown from `planState.plannerText`, rendered
 *      first so the user sees the assistant's intent before the work.
 *   2. For each touched plan node, in start-order: the node's text body
 *      verbatim (live during `started`, persisted after `completed`). The
 *      body itself contains `json-tool-call` fences emitted by the tools
 *      (search, getWebPageContents, queryDocument, …) — those render
 *      inline as `CodeToolCallElement` cards, recreating the V1 chat
 *      timeline of tool-call rows + descriptions.
 *   3. Failed nodes render their persisted/streamed body verbatim. Activity
 *      failures append a markdown error line to that body on the server.
 *
 * Pure function. Consumers (PlanTree + SubAgentCards) call this directly
 * with the reactive-var values they already subscribe to — a wrapping
 * `useHarnessFlattenedText` hook previously existed but had no callers
 * and added a redundant subscription.
 */
export function buildFlattenedMarkdown(planState: PlanState): string {
  const sections: string[] = [];

  const plannerText = planState.plannerText.trim();
  if (plannerText.length > 0) sections.push(plannerText);

  for (const node of getVisibleNodesInOrder(planState)) {
    const { body } = buildNodeMarkdown({ node, planState });
    if (body.length > 0) sections.push(body);
  }

  return sections.join('\n\n');
}

/**
 * Builds the markdown body for a single plan node — the per-node slice
 * of `buildFlattenedMarkdown`. Used by the sub-agent card UI to render
 * one card body at a time via `ChatPanelMarkdown`, reusing the same
 * tool-call / thinking rendering pipeline.
 */
export function buildNodeMarkdown({ node, planState }: { node: PlanNode; planState: PlanState }): {
  body: string;
  status: NodeStatus;
} {
  const nodeState = planState.nodeStates[String(node.id)] ?? null;
  const nodeOutput = planState.nodeOutputs[String(node.id)] ?? null;
  const status = inferNodeStatus({ nodeOutput, nodeState, planState });

  return { body: getNodeOutputDisplayText(nodeOutput).trim(), status };
}

export function getVisibleNodesInOrder(planState: PlanState): PlanNode[] {
  if (!planState.plan) return [];

  const flattenedNodes = flattenPlanNodes(planState.plan);
  const visible = flattenedNodes
    .map(({ node }) => node)
    .filter(node => {
      const nodeState = planState.nodeStates[String(node.id)];
      const output = planState.nodeOutputs[String(node.id)];
      return (
        Boolean(nodeState) ||
        Boolean(output) ||
        getCanInferNodeQueued(planState, node, flattenedNodes)
      );
    });

  return [...visible].sort((a, b) => {
    const aStart = planState.nodeStates[String(a.id)]?.startedAt;
    const bStart = planState.nodeStates[String(b.id)]?.startedAt;
    if (aStart && bStart) return Date.parse(aStart) - Date.parse(bStart);
    if (aStart) return -1;
    if (bStart) return 1;
    return a.id - b.id;
  });
}

/**
 * Decide what status a node is in from server-owned `planState`:
 *   - output with status `streaming`          → STARTED (mid-stream)
 *     snapshot persisted by executeNode's throttle; lets a refreshed
 *     client keep applying live deltas instead of freezing on the
 *     snapshot's displayText)
 *   - completed nodeState                     → COMPLETED
 *   - planState executing AND deps satisfied  → QUEUED until the node's
 *     first persisted lifecycle/text snapshot lands
 */
export function inferNodeStatus({
  nodeOutput,
  nodeState,
  planState,
}: {
  nodeOutput: NodeOutput | null;
  nodeState: PlanNodeLifecycleState | null;
  planState: PlanState;
}): NodeStatus {
  if (nodeState) {
    /**
     * Defense-in-depth for rows finalized before the server force-flip
     * terminalized nodeStates. A non-terminal node (`'started'`
     * mid-stream, or `'awaitingInput'` paused for the user) under a terminal
     * turn would otherwise render a perpetual spinner. The planState can't
     * tell cancel from error here (both surface as `'failed'`), so historical
     * leaks resolve to `failed`; live/new cancels carry their own
     * `'cancelled'` status written at finalize and pass through unchanged.
     */
    const isNonTerminalStatus =
      nodeState.status === PLAN_NODE_LIFECYCLE_STATUSES.started ||
      nodeState.status === PLAN_NODE_LIFECYCLE_STATUSES.awaitingInput;
    if (isNonTerminalStatus && planState.status !== PLAN_STATE_STATUSES.executing) {
      return PLAN_NODE_LIFECYCLE_STATUSES.failed;
    }
    return nodeState.status;
  }
  if (nodeOutput?.status === 'streaming') {
    /*
     * Streaming snapshot. If the workflow is still running it'll overwrite
     * this with a completion write; show STARTED so live deltas continue
     * rendering. If the workflow has already finalized but the snapshot
     * never got cleared (activity died mid-stream), surface as FAILED so
     * the user isn't stuck on a forever-spinning node.
     */
    return planState.status === PLAN_STATE_STATUSES.executing
      ? PLAN_NODE_LIFECYCLE_STATUSES.started
      : PLAN_NODE_LIFECYCLE_STATUSES.failed;
  }
  if (nodeOutput) return PLAN_NODE_LIFECYCLE_STATUSES.completed;
  return 'queued';
}

/**
 * Returns the chat-panel display variant: tool-call fences + prose in the
 * form the user would have seen streaming.
 */
export function getNodeOutputDisplayText(nodeOutput: NodeOutput | null): string {
  return nodeOutput?.displayText ?? '';
}

function getCanInferNodeQueued(
  planState: PlanState,
  node: PlanNode,
  flattenedNodes: ReturnType<typeof flattenPlanNodes>
) {
  if (!planState.plan || planState.status !== 'executing') return false;

  const priorNodeIds = resolvePriorNodeIds(node, flattenedNodes);
  return priorNodeIds.every(id => planState.nodeOutputs[String(id)]);
}
